import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { SessionContext } from '../../../../context/SessionContext'
import { aiChatSupport } from '../../sessionSettings'
import AiChatSettings from '../../studentView/AiChatSettings'
import { draftConcepts } from '../../../../service/experimentService'
import {
  ASSIGNMENT_MODES,
  CONCEPT_DESCRIPTION_MAX,
  CONCEPT_EXAMPLE_MAX,
  CONCEPT_LABEL_MAX,
  CONDITION_NAME_MAX,
  EXPERIMENT_NAME_MAX,
  MAX_CONCEPTS,
  MAX_CONDITIONS,
  MIN_CONDITIONS,
  OFFLOADING_MODES,
  conditionAiChat,
  conditionOffloading,
  conditionPolicy,
  newCondition,
  newConcept,
  usesConcepts,
} from '../../experiment/experimentConfig'

// Last step in experiment mode (the header's Experiment switch). The steps before it author
// what every condition shares; here each condition gets what may differ between them - AI
// chat, its policy, the offloading panel - in a card that expands to its details. Nothing
// here is saved until Publish, which copies the draft into one session per condition.
//
// `drafting` is owned by the wizard, so Publish can wait for an AI draft in flight.
// `onSelectCondition(index)` follows the card being opened, so the preview shows it.
const ExperimentStep = ({
  experiment,
  setExperiment,
  type,
  disabled,
  drafting,
  setDrafting,
  onSelectCondition,
}) => {
  const { session } = useContext(SessionContext)
  const [draftError, setDraftError] = useState('')
  // Open condition cards, by rowId. The first starts open so the details are discoverable.
  const [openRows, setOpenRows] = useState(() => new Set([experiment.conditions[0]?.rowId]))

  const chatSupport = aiChatSupport(type)
  const showsConcepts = usesConcepts(experiment, type)
  const conditions = experiment.conditions

  const update = (patch) => setExperiment((prev) => ({ ...prev, ...patch }))
  const updateCondition = (rowId, patch) =>
    setExperiment((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c) => (c.rowId === rowId ? { ...c, ...patch } : c)),
    }))
  const addCondition = () => {
    const added = newCondition(conditions.length, conditions)
    setExperiment((prev) => ({ ...prev, conditions: [...prev.conditions, added] }))
    setOpenRows((prev) => new Set(prev).add(added.rowId))
    onSelectCondition?.(conditions.length)
  }
  const removeCondition = (rowId) =>
    setExperiment((prev) => ({ ...prev, conditions: prev.conditions.filter((c) => c.rowId !== rowId) }))
  const toggleCondition = (rowId, index) => {
    const opening = !openRows.has(rowId)
    setOpenRows((prev) => {
      const next = new Set(prev)
      if (opening) next.add(rowId)
      else next.delete(rowId)
      return next
    })
    if (opening) onSelectCondition?.(index)
  }

  const updateConcept = (rowId, patch) =>
    setExperiment((prev) => ({
      ...prev,
      concepts: prev.concepts.map((c) => (c.rowId === rowId ? { ...c, ...patch } : c)),
    }))
  const addConcept = () => {
    setDraftError('')
    setExperiment((prev) => ({ ...prev, concepts: [...prev.concepts, newConcept()] }))
  }
  const removeConcept = (rowId) =>
    setExperiment((prev) => ({ ...prev, concepts: prev.concepts.filter((c) => c.rowId !== rowId) }))

  const handleDraftConcepts = async () => {
    setDraftError('')
    setDrafting(true)
    try {
      const drafted = await draftConcepts({
        task: session?.task,
        type: session?.type || type,
        template: session?.test_code?.template,
        testList: session?.test_list,
      })
      if (!drafted.length) {
        setDraftError('The AI did not suggest any concepts. Add them by hand, or try again.')
        return
      }
      update({ concepts: drafted.map((c) => newConcept(c.label, c.description, c.example)) })
    } catch (error) {
      console.error('Drafting the concept list failed:', error)
      setDraftError('Could not draft concepts right now. Add them by hand, or try again in a moment.')
    } finally {
      setDrafting(false)
    }
  }

  // The concept list appears once some condition uses the offloading panel. Draft it from
  // the problem description right then, if it is still empty - once per visit to this step.
  const autoDraftedRef = useRef(false)
  useEffect(() => {
    if (!showsConcepts || autoDraftedRef.current || experiment.concepts.length || drafting) return
    autoDraftedRef.current = true
    handleDraftConcepts()
  }, [showsConcepts]) // eslint-disable-line react-hooks/exhaustive-deps

  // What can differ between conditions, per condition, and which of it does: on every card,
  // a setting that differs between conditions is highlighted and one they all share is muted,
  // so the comparison the experiment makes can be read off the closed cards.
  const settings = conditions.map((condition) => ({
    chat: conditionAiChat(condition, type),
    policy: conditionPolicy(condition, type),
    offloading: conditionOffloading(condition, type),
  }))
  const differs = (key) => settings.some((s) => s[key] !== settings[0][key])
  const differing = { chat: differs('chat'), policy: differs('policy'), offloading: differs('offloading') }
  const identical = chatSupport !== 'unavailable' && !differing.chat && !differing.policy && !differing.offloading

  const OFFLOADING_CHIP = { off: 'No offloading panel', hidden: 'Offloading analyzed', shown: 'Offloading panel' }
  // The chips on one card. A shared "off" is left out to keep the cards quiet.
  const conditionChips = (s) => {
    if (chatSupport === 'unavailable') return [{ key: 'same', label: 'Same as the exercise', diff: false }]
    const chips = [{ key: 'chat', label: s.chat ? 'AI chat' : 'No AI chat', diff: differing.chat }]
    if (s.chat && (s.policy || differing.policy)) {
      chips.push({ key: 'policy', label: s.policy ? 'Policy' : 'No policy', diff: differing.policy })
    }
    if (s.offloading !== 'off' || differing.offloading) {
      chips.push({ key: 'offloading', label: OFFLOADING_CHIP[s.offloading], diff: differing.offloading })
    }
    return chips
  }

  return (
    <>
      <div className="cq-card__head">
        <div>
          <h2>Experiment</h2>
          <p>Split students between versions of this exercise and compare them.</p>
        </div>
      </div>

      <Alert severity="info" sx={{ mb: 2 }}>
        Every condition gets the problem, tests and starter code from the steps above. Set what differs
        between them here. Publishing copies the exercise into one session per condition; after that you can
        open any condition&apos;s session and change anything in it.
      </Alert>

      <div className="cq-field">
        <TextField
          label="Experiment name"
          value={experiment.name}
          onChange={(e) => update({ name: e.target.value })}
          inputProps={{ maxLength: EXPERIMENT_NAME_MAX }}
          helperText="For you and your team. Students only ever see the problem name."
          fullWidth
          size="small"
          disabled={disabled}
        />
      </div>

      <div className="cq-field">
        <div className="cq-field__label">
          <span>Conditions</span>
          <span className="cq-saved-hint">
            {conditions.length} of {MAX_CONDITIONS}
          </span>
        </div>
        {chatSupport !== 'unavailable' && (
          <div className="cq-chip-legend">
            <span className="cq-chip cq-chip--diff">Highlighted</span> differs between conditions
            <span className="cq-chip cq-chip--same">Grey</span> same in every condition
          </div>
        )}
        {identical && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            These conditions are identical. Open one and change a setting so the experiment compares something.
          </Alert>
        )}
        <ol className="cq-conditions">
          {conditions.map((condition, index) => {
            const open = openRows.has(condition.rowId)
            const chatOn = conditionAiChat(condition, type)
            const bodyId = `cq-condition-${condition.rowId}`
            const label = condition.name.trim() || `Condition ${index + 1}`
            return (
              <li key={condition.rowId} className={`cq-condition${open ? ' is-open' : ''}`}>
                <div className="cq-condition__head">
                  <button
                    type="button"
                    className="cq-condition__toggle"
                    aria-expanded={open}
                    aria-controls={bodyId}
                    onClick={() => toggleCondition(condition.rowId, index)}>
                    <ExpandMoreIcon className="cq-condition__chevron" fontSize="small" />
                    <span className="cq-condition__name">{label}</span>
                    <span className="cq-condition__chips">
                      {conditionChips(settings[index]).map((chip) => (
                        <span
                          key={chip.key}
                          className={`cq-chip ${chip.diff ? 'cq-chip--diff' : 'cq-chip--same'}`}
                          title={chip.diff ? 'Differs between conditions' : 'Same in every condition'}>
                          {chip.label}
                        </span>
                      ))}
                    </span>
                  </button>
                  <Tooltip
                    title={
                      conditions.length > MIN_CONDITIONS
                        ? 'Remove this condition'
                        : `An experiment needs at least ${MIN_CONDITIONS} conditions`
                    }>
                    <span>
                      <IconButton
                        aria-label={`Remove ${label}`}
                        onClick={() => removeCondition(condition.rowId)}
                        disabled={disabled || conditions.length <= MIN_CONDITIONS}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </div>

                {open && (
                  <div className="cq-condition__body" id={bodyId}>
                    <TextField
                      label="Condition name"
                      value={condition.name}
                      onChange={(e) => updateCondition(condition.rowId, { name: e.target.value })}
                      inputProps={{ maxLength: CONDITION_NAME_MAX }}
                      size="small"
                      fullWidth
                      disabled={disabled}
                      sx={{ mb: 1.5 }}
                    />
                    <AiChatSettings
                      type={type}
                      enabled={chatOn}
                      onEnabledChange={(on) => updateCondition(condition.rowId, { aiChat: on })}
                      policy={condition.aiPolicy}
                      onPolicyChange={(text) => updateCondition(condition.rowId, { aiPolicy: text })}
                      disabled={disabled}
                      dense
                      helpId={`${bodyId}-chat-help`}
                    />
                    {chatOn && (
                      <TextField
                        select
                        label="Offloading panel"
                        value={condition.offloading}
                        onChange={(e) => updateCondition(condition.rowId, { offloading: e.target.value })}
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                        disabled={disabled}
                        helperText="Tracks which task concepts the student worked out themselves vs. got from the AI.">
                        {OFFLOADING_MODES.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ol>
        <Button
          startIcon={<AddIcon />}
          onClick={addCondition}
          disabled={disabled || conditions.length >= MAX_CONDITIONS}
          size="small">
          Add condition
        </Button>
      </div>

      <div className="cq-field">
        <div className="cq-field__label">
          <span>Assigning students</span>
        </div>
        <RadioGroup
          value={experiment.assignment}
          onChange={(e) => update({ assignment: e.target.value })}
          aria-label="Assigning students">
          {ASSIGNMENT_MODES.map((mode) => (
            <FormControlLabel
              key={mode.value}
              value={mode.value}
              disabled={disabled}
              control={<Radio size="small" />}
              label={
                <span className="cq-option">
                  <span className="cq-option__label">{mode.label}</span>
                  <span className="cq-saved-hint">{mode.description}</span>
                </span>
              }
            />
          ))}
        </RadioGroup>
      </div>

      {showsConcepts && (
        <div className="cq-field">
          <div className="cq-field__label">
            <span>
              Concepts<span className="cq-required">*</span>
            </span>
            <Button
              size="small"
              startIcon={drafting ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
              onClick={handleDraftConcepts}
              disabled={disabled || drafting}>
              {experiment.concepts.length ? 'Replace with AI draft' : 'Draft with AI'}
            </Button>
          </div>
          <div className="cq-saved-hint" style={{ marginBottom: 10 }}>
            {drafting
              ? 'Drafting concepts from the problem description…'
              : 'The ideas a student has to work out for this exercise, drafted from the problem description. The offloading panel tracks each one as worked out by the student, with the AI, or by the AI. Name ideas, not solution steps.'}
          </div>
          {draftError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {draftError}
            </Alert>
          )}
          <ol className="cq-concepts">
            {experiment.concepts.map((concept, index) => (
              <li key={concept.rowId} className="cq-concept">
                <div className="cq-concept__fields">
                  <TextField
                    label={`Concept ${index + 1}`}
                    value={concept.label}
                    onChange={(e) => updateConcept(concept.rowId, { label: e.target.value })}
                    inputProps={{ maxLength: CONCEPT_LABEL_MAX }}
                    size="small"
                    fullWidth
                    disabled={disabled || drafting}
                  />
                  <TextField
                    label="What understanding it means"
                    value={concept.description}
                    onChange={(e) => updateConcept(concept.rowId, { description: e.target.value })}
                    inputProps={{ maxLength: CONCEPT_DESCRIPTION_MAX }}
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    disabled={disabled || drafting}
                  />
                  <TextField
                    label="Example (optional)"
                    value={concept.example || ''}
                    onChange={(e) => updateConcept(concept.rowId, { example: e.target.value })}
                    inputProps={{ maxLength: CONCEPT_EXAMPLE_MAX }}
                    helperText={
                      index === 0 ? 'Shown with the description when a student hovers a concept in the panel.' : undefined
                    }
                    size="small"
                    fullWidth
                    disabled={disabled || drafting}
                  />
                </div>
                <IconButton
                  aria-label={`Remove concept ${index + 1}`}
                  onClick={() => removeConcept(concept.rowId)}
                  disabled={disabled || drafting}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </li>
            ))}
          </ol>
          <Button
            startIcon={<AddIcon />}
            onClick={addConcept}
            disabled={disabled || drafting || experiment.concepts.length >= MAX_CONCEPTS}
            size="small">
            Add concept
          </Button>
        </div>
      )}
    </>
  )
}

export default ExperimentStep
